import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLanguage, LanguageSelector } from "../../i18n";
import { LOGO_NAVBAR } from "../../constants";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, language } = useLanguage();
  const location = useLocation();
  
  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Helper to create proper href - if not on home, prefix with /
  const getHref = (anchor) => {
    return isHomePage ? anchor : `/${anchor}`;
  };

  // Desktop navigation links
  // Navigation links - simplified without duplicate service buttons
  const navLinks = [
    { href: isHomePage ? "#sky-meets-water" : "/elements", label: t('nav.skyMeetsWater'), isElements: true },
    { href: isHomePage ? "#proceso" : "/#proceso", label: t('nav.process') },
    { href: isHomePage ? "#aplicaciones" : "/#aplicaciones", label: t('nav.applications') },
    { href: isHomePage ? "#faq" : "/#faq", label: t('nav.faq') },
  ];

  // Mobile navigation links
  const mobileNavLinks = [
    { href: isHomePage ? "#experiencias" : "/show-de-drones", label: 'Drone Show', isSpecial: true },
    { href: isHomePage ? "#water-screen" : "/pantalla-de-agua", label: 'Water Screen', isSpecial: true },
    { href: isHomePage ? "#special-fx" : "/efectos-especiales", label: 'Special FX', isSpecial: true },
    { href: isHomePage ? "#sky-meets-water" : "/elements", label: t('nav.skyMeetsWater'), isSpecial: false },
  ];

  const handleNavClick = (e, href) => {
    // If it's a hash link on home page, scroll smoothly
    if (href.startsWith('#') && isHomePage) {
      setIsMenuOpen(false);
      return; // Let browser handle scroll
    }
    // If it's a path link, navigate
    if (href.startsWith('/')) {
      setIsMenuOpen(false);
      return; // Let Link handle navigation
    }
    // If not on home page and it's a hash link with /
    if (!isHomePage && href.startsWith('/#')) {
      setIsMenuOpen(false);
      window.location.href = href;
      return;
    }
    setIsMenuOpen(false);
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass py-3" : "bg-[#030810] md:bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link 
          to="/" 
          className="flex items-center gap-3 flex-shrink-0" 
          data-testid="nav-logo"
          onClick={() => {
            if (isHomePage) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              window.scrollTo(0, 0);
            }
          }}
        >
          <img src={LOGO_NAVBAR} alt="NexAir Elements" className="h-12 md:h-14 w-auto" />
        </Link>

        {/* Desktop Navigation - spread across header */}
        <div className="hidden md:flex items-center justify-end flex-1 gap-6 ml-8">
          {navLinks.map((link) => {
            const isExternalOrHash = link.href.startsWith('#') || link.href.startsWith('/#');
            const NavComponent = isExternalOrHash ? 'a' : Link;
            const navProps = isExternalOrHash 
              ? { href: getHref(link.href) }
              : { to: link.href };
            
            return (
              <NavComponent
                key={link.href}
                {...navProps}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`nav-link font-medium text-sm transition-all ${
                  link.isElements 
                    ? 'text-white px-4 py-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 shadow-lg hover:shadow-cyan-500/30' 
                    : 'text-gray-300 hover:text-white'
                }`}
                style={{ fontFamily: "'Orbitron', sans-serif", letterSpacing: '0.03em', fontSize: '0.8rem' }}
                data-testid={`nav-${link.href.replace('#', '').replace('/', '')}`}
              >
                {link.label}
              </NavComponent>
            );
          })}
          <a
            href={getHref("#contacto")}
            onClick={(e) => handleNavClick(e, "#contacto")}
            className="bg-white/10 backdrop-blur-sm border border-cyan-400/50 hover:bg-cyan-400/20 text-cyan-400 font-semibold py-2 px-5 rounded-full transition-all text-sm"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
            data-testid="nav-cotiza"
          >
            {language === 'es' ? 'Cotiza' : 'Quote'}
          </a>
          <a
            href={getHref("#visualiza")}
            onClick={(e) => handleNavClick(e, "#visualiza")}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold py-2 px-5 rounded-full transition-all hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] text-sm"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
            data-testid="nav-cta"
          >
            {language === 'es' ? 'Simular Campaña' : 'Simulate Campaign'}
          </a>
          <LanguageSelector />
        </div>

        {/* Mobile Menu Button */}
        <div className="flex md:hidden items-center gap-3">
          <LanguageSelector />
          <button
            className="text-white p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden glass mt-2 mx-4 rounded-xl p-6" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {mobileNavLinks.map((link) => (
              <a
                key={link.href}
                href={getHref(link.href)}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`font-medium py-2 transition-colors flex items-center gap-2 ${
                  link.isSpecial 
                    ? 'text-cyan-400 hover:text-cyan-300' 
                    : 'text-gray-300 hover:text-cyan-400'
                }`}
                style={{ fontFamily: "'Orbitron', sans-serif", letterSpacing: '0.05em' }}
              >
                {link.isSpecial && (
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                )}
                {link.label}
              </a>
            ))}
            <div className="flex gap-3 mt-2">
              <a
                href={getHref("#contacto")}
                onClick={(e) => handleNavClick(e, "#contacto")}
                className="flex-1 bg-white/10 backdrop-blur-sm border border-cyan-400/50 text-cyan-400 font-semibold py-3 px-4 rounded-full text-center text-sm"
                style={{ fontFamily: "'Orbitron', sans-serif" }}
              >
                {language === 'es' ? 'Cotiza' : 'Quote'}
              </a>
              <a
                href={getHref("#visualiza")}
                onClick={(e) => handleNavClick(e, "#visualiza")}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold py-3 px-4 rounded-full text-center text-sm"
                style={{ fontFamily: "'Orbitron', sans-serif" }}
              >
                {language === 'es' ? 'Simular' : 'Simulate'}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
